import { Box, Typography } from "@mui/material";
import { ExternalLink } from "ExternalLink";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useTiers } from "api/tiers";
import { useAppSelector } from "app/hooks";
import { CustomAttributesSection } from "components/CustomAttributesSection/CustomAttributesSection";
import { FormButtons } from "components/FormControls/FormButtons";
import { sortCampaignTranslations } from "components/Layout/Campaign/Common/campaignHelpers";
import { InfoBox } from "components/Layout/InfoBox";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useNavigate, useParams } from "react-router-dom";
import { selectDefaultLanguageData } from "reduxState/translations/translationsSlice";
import { BETierSetFormat, FETierSetFormat, TierSetType } from "types/tierSets";
import {
  backendErrors,
  parseTranslationsArrayError,
} from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { formatDowngradeMode, parseDowngradeMode } from "../utils";
import { TierSetConditions } from "./TierSetConditions/TierSetConditions";
import { TierSetDowngradeConditions } from "./TierSetDowngradeConditions";
import { useStyles } from "./TierSetForm.styles";
import { TierSetSettings } from "./TierSetSettings/TierSetSettings";
import { TierSetStatus } from "./TierSetStatus";

interface Props {
  editMode?: boolean;
  tierSetData?: TierSetType;
}

export const TierSetForm = ({ editMode, tierSetData }: Props) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const defaultLang = useAppSelector(selectDefaultLanguageData);
  const { id: tierSetId } = useParams<{ id: string }>();
  const { addTierSet, editTierSet } = useTiers();

  const initialValues: FETierSetFormat =
    editMode && tierSetData
      ? {
          translations:
            tierSetData.translations &&
            sortCampaignTranslations(
              defaultLang?.code,
              Object.keys(tierSetData.translations).map((key) => ({
                name: tierSetData?.translations[key]?.name,
                description: tierSetData?.translations[key]?.description,
                code: key,
              })),
            ),
          active: tierSetData.active,
          labels: tierSetData.labels,
          conditions: tierSetData.conditions,
          downgrade: parseDowngradeMode(tierSetData.downgrade),
          isMigrated: tierSetData.isMigrated,
        }
      : {
          translations: [
            defaultLang && {
              name: "",
              description: "",
              code: defaultLang.code,
            },
          ],
          active: false,
          labels: [],
          conditions: [],
          downgrade: {
            mode: "none",
          },
        };

  const transformTranslations = (
    translations: FETierSetFormat["translations"],
  ): {} => {
    return translations.reduce(
      (acc, cur) => ({
        ...acc,
        [cur.code as string]: {
          name: cur.name,
          description: cur.description,
        },
      }),
      {},
    );
  };

  return (
    <Formik
      enableReinitialize
      initialStatus={{}}
      initialValues={initialValues}
      onSubmit={async (values, { setSubmitting, setStatus }) => {
        setSubmitting(true);
        setStatus({});

        const properties: BETierSetFormat = {
          ...values,
          downgrade: formatDowngradeMode(values.downgrade),
          translations: transformTranslations(values.translations),
          isMigrated: undefined,
        };

        try {
          if (editMode) {
            await editTierSet(tierSetId as string, properties);
            navigate(links.tierSet(tierSetId));
            notify(
              message({
                id: "tierSetUpdated",
              }),
            );
          } else {
            const response = await addTierSet(properties);
            const id = response.data.tierSetId as string;
            navigate(links.tierSet(id));
            notify(
              message({
                id: "tierSetCreated",
              }),
            );
          }
        } catch (exception) {
          setSubmitting(false);
          notify(getErrorMessage(exception));
          const errors = backendErrors(getBackendErrors(exception));
          const parsedErrors = {
            ...errors,
            translations: parseTranslationsArrayError(
              errors?.translations,
              values?.translations,
            ),
          };

          setStatus(parsedErrors);
          focusError();
        }
      }}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({
        handleSubmit,
        isSubmitting,
        status,
        values,
        dirty,
        setFieldValue,
        initialValues,
      }) => {
        const conditionsError =
          status.conditions &&
          Array.isArray(status.conditions) &&
          typeof status.conditions[0] === "string"
            ? status.conditions[0]
            : undefined;

        return (
          <Form onSubmit={handleSubmit}>
            {status?.[""] && (
              <span className={`${classes.error} error-anchor`}>
                {status[""]}
              </span>
            )}
            <Typography className={classes.sectionHeader} mb={8}>
              {message({ id: "tiersSetSettings" })}
            </Typography>
            <TierSetSettings values={values} />

            <CustomAttributesSection labels={values.labels || []} />

            {editMode && (
              <InfoBox
                type="warning"
                text={message({
                  id: "tierConditionsChangeWarning",
                })}
                className={classes.marginBottom}
              />
            )}

            <Typography className={classes.sectionHeader} mb={8}>
              {message({ id: "tiersSetConditions" })}
            </Typography>
            {conditionsError && (
              <Typography className={`${classes.error}`}>
                {conditionsError}
              </Typography>
            )}
            <TierSetConditions />

            <Typography className={classes.sectionHeader} mb={4}>
              {message({ id: "tiersSetDowngradeConditions" })}
            </Typography>
            <Typography className={classes.sectionSubheader} mb={8}>
              {message(
                { id: "tiersSetDowngradeConditionsInfo" },
                {
                  link: (
                    <ExternalLink path="https://help.openloyalty.io/main-features/tiers">
                      {message({
                        id: "learnMoreAboutTiers",
                      })}
                    </ExternalLink>
                  ),
                },
              )}
            </Typography>
            <TierSetDowngradeConditions
              setFieldValue={setFieldValue}
              downgradeMode={values.downgrade}
            />

            <Typography className={classes.sectionHeader} mb={8}>
              {message({ id: "tiersSetStatus" })}
            </Typography>
            <TierSetStatus />

            <Box mt={12} />
            <FormButtons
              disableSubmit={isSubmitting}
              onCancelClick={() => navigate(links.tierSetList)}
              onSubmitClick={() => handleSubmit()}
              {...(!editMode && { submitMessage: "addTierSet" })}
            />

            {!isSubmitting && (
              <LeaveSiteGuard
                warnBeforeRefresh
                isDirty={dirty}
                initialValues={initialValues}
                currentValues={values}
              />
            )}
          </Form>
        );
      }}
    </Formik>
  );
};
